import { useRef, useState, type ReactNode } from 'react';
import { Box, IconButton, ListItemIcon, Menu, MenuItem } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { useOverflowTabs } from '../../hooks/useOverflowTabs';

export type DetailFormActionItem = {
  key: string;
  label: string;
  icon?: ReactNode;
};

type DetailFormActionOverflowProps = {
  items: DetailFormActionItem[];
  renderItem: (index: number) => ReactNode;
  onSelect: (index: number, anchorEl: HTMLElement) => void;
  isItemDisabled?: (index: number) => boolean;
  isItemDestructive?: (index: number) => boolean;
  hiddenItemKeys?: string[];
  moreButtonSx?: SxProps<Theme>;
};

export const DetailFormActionOverflow = ({
  items,
  renderItem,
  onSelect,
  isItemDisabled,
  isItemDestructive,
  hiddenItemKeys = [],
  moreButtonSx,
}: DetailFormActionOverflowProps) => {
  const hiddenKeys = new Set(hiddenItemKeys);
  const inlineIndices = items.reduce<number[]>(
    (indices, item, index) => (hiddenKeys.has(item.key) ? indices : [...indices, index]),
    []
  );
  const menuOnlyItemIndices = items.reduce<number[]>(
    (indices, item, index) => (hiddenKeys.has(item.key) ? [...indices, index] : indices),
    []
  );
  const actionsRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const { displayedIndices, hiddenIndices } = useOverflowTabs({
    itemCount: inlineIndices.length,
    activeIndex: -1,
    containerRef: actionsRef,
    measurementRef,
    moreWidth: 42,
    alwaysReserveMore: menuOnlyItemIndices.length > 0,
    minimumHiddenCount: menuOnlyItemIndices.length > 0 ? 0 : 2,
    gap: 8,
  });
  const displayedItemIndices = displayedIndices.map((index) => inlineIndices[index]);
  const overflowItemIndices = [
    ...hiddenIndices.map((index) => inlineIndices[index]),
    ...menuOnlyItemIndices,
  ];

  return (
    <>
      <Box
        ref={actionsRef}
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          minWidth: 0,
          flex: '1 1 auto',
          maxWidth: '100%',
          marginLeft: 'auto',
          justifyContent: 'flex-end',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {displayedItemIndices.map((index) => renderItem(index))}
        {overflowItemIndices.length > 0 && (
          <IconButton
            size="small"
            color="primary"
            aria-label={`Show ${overflowItemIndices.length} more actions`}
            onClick={(event) => setMoreAnchor(event.currentTarget)}
            sx={{
              flex: '0 0 auto',
              border: 1,
              borderColor: 'primary.main',
              borderRadius: 1,
              color: 'primary.main',
              ...moreButtonSx,
            }}
          >
            <MoreHoriz fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box
        ref={measurementRef}
        aria-hidden
        sx={{ position: 'absolute', visibility: 'hidden', top: -10000, whiteSpace: 'nowrap' }}
      >
        {inlineIndices.map((itemIndex, index) => (
          <Box
            key={items[itemIndex].key}
            data-overflow-tab-index={index}
            sx={{ display: 'inline-flex' }}
          >
            {renderItem(itemIndex)}
          </Box>
        ))}
      </Box>
      {overflowItemIndices.length > 0 && (
        <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
          {overflowItemIndices.map((index) => (
            <MenuItem
              key={items[index].key}
              disabled={isItemDisabled?.(index)}
              sx={isItemDestructive?.(index) ? { color: 'error.main' } : undefined}
              onClick={(event) => {
                setMoreAnchor(null);
                onSelect(index, event.currentTarget);
              }}
            >
              {items[index].icon && (
                <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                  {items[index].icon}
                </ListItemIcon>
              )}
              {items[index].label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};
